import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/auth";
import { ScrollText, RefreshCw, Search } from "lucide-react";

const inp = "w-full bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none";
const STATUS = { sent: "text-emerald-400", failed: "text-red-400", skipped: "text-zinc-500", blocked: "text-amber-400" };
const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Melbourne", day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" });

export default function EmailLog() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [outcome, setOutcome] = useState("");
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/admin/email-log", { params: { q, kind, outcome, days } }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q, kind, outcome, days]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div className="p-4 sm:p-8 max-w-[1300px]" data-testid="email-log-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2"><ScrollText size={12}/> Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase">Email log</h1>
          <p className="text-zinc-400 mt-2 text-sm">Every email the system sent or tried to send — to whom, when, and whether it went through. To stop or reword a type, open <Link to="/admin/email-settings" className="text-red-400 underline">Email Settings</Link>.</p>
        </div>
        <button type="button" onClick={load} className="text-[11px] uppercase tracking-widest border border-white/20 px-3 py-2 hover:border-white/50 flex items-center gap-2" data-testid="email-log-refresh"><RefreshCw size={12} className={loading ? "animate-spin" : ""}/>Refresh</button>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-6" data-testid="email-log-summary">
          {data.by_kind.map(k => (
            <button type="button" key={k.kind} onClick={() => setKind(kind === k.kind ? "" : k.kind)} className={`text-left border p-3 ${kind === k.kind ? "border-red-600 bg-red-600/10" : "border-white/10 bg-black/40 hover:border-white/30"}`} data-testid={`email-log-kind-${k.kind}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">{k.label}</div>
              <div className="font-display text-2xl mt-1">{k.sent}<span className="text-sm text-zinc-500"> sent</span></div>
              {(k.failed || k.blocked) ? <div className="text-[11px] text-red-400">{k.failed + (k.blocked || 0)} failed</div> : null}
            </button>
          ))}
          {!data.by_kind.length && <div className="text-zinc-500 text-sm col-span-full">No emails sent in the last {days} days.</div>}
        </div>
      )}

      <div className="grid sm:grid-cols-4 gap-2 mb-4">
        <div className="relative sm:col-span-2"><Search size={14} className="absolute left-3 top-2.5 text-zinc-500"/><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by email or subject" className={`${inp} pl-9`} data-testid="email-log-search" /></div>
        <select value={outcome} onChange={e => setOutcome(e.target.value)} className={inp} data-testid="email-log-status">
          <option value="">All outcomes</option><option value="sent">Sent</option><option value="failed">Failed</option><option value="blocked">Blocked</option><option value="skipped">Skipped</option>
        </select>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className={inp} data-testid="email-log-days">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last year</option>
        </select>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm" data-testid="email-log-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
            <th className="px-4 py-3">When (AEST)</th><th className="px-4 py-3">To</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Outcome</th>
          </tr></thead>
          <tbody>
            {(data?.rows || []).map(r => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`email-log-row-${r.id}`}>
                <td className="px-4 py-2.5 text-zinc-400 whitespace-nowrap">{fmt(r.at)}</td>
                <td className="px-4 py-2.5 text-zinc-200">{r.to}</td>
                <td className="px-4 py-2.5 text-zinc-400">{data.labels[r.kind] || r.kind}</td>
                <td className="px-4 py-2.5 text-zinc-300 max-w-[380px] truncate" title={r.subject}>{r.subject}</td>
                <td className={`px-4 py-2.5 capitalize ${STATUS[r.status] || ""}`} title={r.error || ""}>{r.status}{r.error ? <span className="block text-[10px] text-zinc-500 normal-case truncate max-w-[200px]">{r.error}</span> : null}</td>
              </tr>
            ))}
            {data && !data.rows.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">Nothing matches.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-zinc-600 text-[11px] mt-3">Log entries are kept for 12 months. Logging started when this feature was added — older emails aren't listed.</p>
    </div>
  );
}
